// Pure text-matching helpers for search-based redaction marking (FIND_MATCH_RECTS).
// Phrase semantics follow the main app's SearchIndexService: terms are the query
// split on whitespace/slash/backslash/hyphen, lowercased; single-char terms are kept beside a longer term (the index
// drops them; marking must not, visiodocs-app#603) and a query of single chars alone finds nothing;
// a phrase match is the terms in order separated by whitespace/slash/backslash/hyphen;
// each match is expanded to whole word boundaries (never under-redact word fragments).
// Both .toLowerCase() and the 'i' regex flag are kept to mirror the main app.

import { MatchPattern } from './shared/model/docavea-viewer-interface';

export interface PhraseMatch {
    start: number;  // inclusive offset in pageText
    end: number;    // exclusive offset in pageText
}

const WORD_CHAR = /[\p{L}\p{N}]/u;

function escapeRegExp(text: string): string {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function buildPhraseRegex(query: string): RegExp | null {
    const words = query
        .replace(/[/\\-]/g, ' ')
        .toLowerCase()
        .split(/\s/)
        .filter((term) => term.length > 0);
    // A one-letter word counts only beside a longer one (visiodocs-app#603): "8200 Aarhus N", "v/Bo", "dansk i firmaets".
    // Dropped there, the box stops short of it or the phrase is found nowhere. A search of one letter alone stays dropped.
    const terms = words.some((term) => term.length > 1) ? words : [];
    if (terms.length === 0) {
        return null;
    }
    // Inside a term, tolerate a hyphenation break between any two characters — a regular
    // or soft hyphen optionally followed by whitespace — so words split across lines
    // ("Frede-\nriksen") still match "frederiksen". The hyphen is required, so plain
    // whitespace inside a term still never matches.
    const termPattern = (term: string) => term.split('').map(escapeRegExp).join('(?:[-\\u00AD]\\s*)?');
    return new RegExp(terms.map(termPattern).join('[\\s\\/\\\\-]+'), 'gi');
}

export function findPhraseMatches(pageText: string, query: string): PhraseMatch[] {
    const regex = buildPhraseRegex(query);
    if (regex === null) {
        return [];
    }
    const matches: PhraseMatch[] = [];
    let m: RegExpExecArray | null;
    while ((m = regex.exec(pageText)) !== null) {
        const rawStart = m.index;
        const rawEnd = m.index + m[0].length;
        // Drop any non-word chars picked up literally from punctuation in the query
        // (e.g. searching "(100)" should redact the word "100", not the parens).
        let start = rawStart;
        let end = rawEnd;
        while (start < end && !WORD_CHAR.test(pageText[start])) start++;
        while (end > start && !WORD_CHAR.test(pageText[end - 1])) end--;
        while (start > 0 && WORD_CHAR.test(pageText[start - 1])) start--;
        while (end < pageText.length && WORD_CHAR.test(pageText[end])) end++;
        if (start < end) {
            matches.push({ start, end });
        }
        regex.lastIndex = Math.max(end, regex.lastIndex); // skip past the match; never rescan
    }
    return matches;
}

const CONTEXT_CHARS = 30;

// Redaction v2 (visiodocs-app#611): the words a match covers and up to `chars` characters of the page's text on either
// side, for the app's find-and-mark bar, so the lawyer knows what a box covers even where the page shows nothing (white or
// tiny text). Runs of whitespace, line breaks included, read as one space. The context is cut at a word, never inside one.
export function matchContext(pageText: string, match: PhraseMatch, chars = CONTEXT_CHARS): { before: string; text: string; after: string } {
    const flat = (s: string) => s.replace(/\s+/g, ' ');
    let from = Math.max(0, match.start - chars);
    while (from > 0 && from < match.start && WORD_CHAR.test(pageText[from - 1])) from++;
    let to = Math.min(pageText.length, match.end + chars);
    while (to < pageText.length && to > match.end && WORD_CHAR.test(pageText[to])) to--;
    return {
        before: flat(pageText.slice(from, match.start)).trimStart(),
        text: flat(pageText.slice(match.start, match.end)),
        after: flat(pageText.slice(match.end, to)).trimEnd(),
    };
}

// Redaction v2 (visiodocs-app#493): the patterns the interactive search finds besides words, starting with CPR numbers.
// A CPR number is ddmmyy-xxxx, written with or without the dash. With a dash (any hyphen or dash, spaces around it, a
// line break after it) it is proposed whatever the digits: a digit OCR misread in the text is still the right number in
// the picture, and missing it leaks it. Without a dash the first six digits must read as a day and month, or every
// ten-digit account and invoice number would be proposed too. Digits on either side mean a longer number, not a CPR number.
const CPR_REGEX = /(?<!\p{N})(\d{2})(\d{2})\d{2}(\s*[-\u2010-\u2013\u2212]\s*|\s?)\d{4}(?!\p{N})/gu;

export function findPatternMatches(pageText: string, pattern: MatchPattern): PhraseMatch[] {
    if (pattern !== 'cpr') {
        throw new Error(`Unknown pattern ${pattern}`); // never "no hits": that would read as "no CPR numbers here"
    }
    const regex = new RegExp(CPR_REGEX); // own lastIndex
    const matches: PhraseMatch[] = [];
    let m: RegExpExecArray | null;
    while ((m = regex.exec(pageText)) !== null) {
        const day = Number(m[1]);
        const month = Number(m[2]);
        const dashed = m[3].trim() !== '';
        if (dashed || (day >= 1 && day <= 31 && month >= 1 && month <= 12)) {
            matches.push({ start: m.index, end: m.index + m[0].length });
        }
    }
    return matches;
}
